import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type {
  TutorAttendanceRatesFilterParams,
  TutorAttendanceRatesFilters,
  TutorAttendanceRatesQueryRules,
} from '@ts-tutor-attendance-rates/interfaces'

import { Status as BookingStatus } from '@brighterly/lib-core-types/booking/enums'
import { TutorAttendanceRateReason } from '@ts-tutor-attendance-rates/enums'

import { queryToDateTimeBounds } from '@helpers/date-range-query'
import { isId, normalizeArrayQuery, toIdValue } from '@helpers/route'

/** Array filters that links from app-main and the Tutor Kanban encode as `key[0]=…`. */
export const TUTOR_ATTENDANCE_RATES_ARRAY_QUERY_KEYS: readonly string[] = ['tutor', 'status', 'reasons']

const TUTOR_ATTENDANCE_RATES_FILTER_QUERY_KEYS: readonly string[] = ['tutor', 'status', 'reasons', 'from', 'to']
const BOOKING_STATUSES: readonly string[] = Object.values(BookingStatus)
const ATTENDANCE_REASONS: readonly string[] = Object.values(TutorAttendanceRateReason)

export const isBookingStatus = (value: string): value is BookingStatus => BOOKING_STATUSES.includes(value)

export const isTutorAttendanceRateReason = (value: string): value is TutorAttendanceRateReason =>
  ATTENDANCE_REASONS.includes(value)

/** While the lists are unavailable nothing is checked against them. */
const isKnownKey = (known: Record<string, string> | null | undefined): ((value: string) => boolean) =>
  value => !known || Object.keys(known).includes(value)

export const emptyTutorAttendanceRatesFilters = (): TutorAttendanceRatesFilters => ({
  tutorIds: [],
  statuses: [],
  reasons: [],
  from: null,
  to: null,
})

export const queryToTutorAttendanceRatesFilters = (
  query: Record<string, unknown>,
  rules: TutorAttendanceRatesQueryRules = {},
): TutorAttendanceRatesFilters => ({
  tutorIds: (normalizeArrayQuery(query.tutor) ?? []).map(toIdValue).filter(isId),
  statuses: (normalizeArrayQuery(query.status) ?? [])
    .filter(isBookingStatus)
    .filter(isKnownKey(rules.statusLabels)),
  reasons: (normalizeArrayQuery(query.reasons) ?? [])
    .filter(isTutorAttendanceRateReason)
    .filter(isKnownKey(rules.reasonLabels)),
  ...queryToDateTimeBounds(query.from, query.to),
})

export const getTutorAttendanceRatesFilterParams = (filters: TutorAttendanceRatesFilters): TutorAttendanceRatesFilterParams => ({
  tutor: filters.tutorIds.length ? filters.tutorIds : undefined,
  status: filters.statuses.length ? filters.statuses : undefined,
  reasons: filters.reasons.length ? filters.reasons : undefined,
  from: filters.from || undefined,
  to: filters.to || undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const tutorAttendanceRatesFiltersToQuery = (filters: TutorAttendanceRatesFilters): LocationQueryRaw => {
  const params = getTutorAttendanceRatesFilterParams(filters)

  return {
    ...params,
    tutor: params.tutor?.map(String),
  }
}

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

/**
 * The query with its filters written the way `filters` reads them, or `null` when it already is. A value
 * the filters dropped (unknown status or reason, lone `to`…) must leave the URL too,
 * or the URL and the controls would disagree.
 */
export const getCanonicalTutorAttendanceRatesQuery = (
  query: LocationQuery,
  filters: TutorAttendanceRatesFilters,
): LocationQueryRaw | null => {
  const canonical = tutorAttendanceRatesFiltersToQuery(filters)
  const isCanonical = TUTOR_ATTENDANCE_RATES_FILTER_QUERY_KEYS
    .every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}
